import { Builder } from '@builder.io/sdk';
import { BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES } from '../BuilderComponentsName';

import {
    buttonVariantOptions,
    textColorOptionsLimited,
    backgroundGradientOptions,
    iconColorOptions,
    waveColorOptions,
} from '../utils/builderColorOptions';
import { getBackgroundGradientInput, getPaddingInputs, getBackgroundColorInput } from '../utils/builderSharedInputs';
import { S4WhyChooseSection } from '../components/interactive-sections/S4WhyChooseSection';

Builder.registerComponent(S4WhyChooseSection, {
    name: BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S4WhyChoose,
    friendlyName: 'S4 Why Choose',
    inputs: [
        {
            name: 'variant',
            type: 'string',
            friendlyName: 'Display Variant',
            enum: [
                { label: 'Default (Icon List)', value: 'default' },
                { label: 'Brand Partner (Text Steps)', value: 'brandPartner' },
            ],
            defaultValue: 'default',
            helperText:
                'Default: Shows icon list with optional text. Brand Partner: Shows numbered text steps with title and description',
            onChange: (options) => {
                const currentVariant = options.get('variant');
                const otherVariant = currentVariant === 'default' ? 'brandPartner' : 'default';

                const defaults: Record<string, Record<string, string | boolean>> = {
                    headline: {
                        default: 'Experience the Future of Laser Hair Removal',
                        brandPartner: '3 Steps To Forever-Smooth Skin',
                    },
                    optionalText: {
                        default:
                            'Our advanced laser technology provides precise, long-lasting results with minimal discomfort. Discover how our innovative approach is revolutionizing hair removal treatments.',
                        brandPartner: '',
                    },
                    imageSrc: {
                        default:
                            'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F88ad4469180d4d399b5371f7b8c5ae5c',
                        brandPartner:
                            'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fc2e244ceff74452a8585546d07efd58c',
                    },
                    imageFlip: {
                        default: false,
                        brandPartner: true,
                    },
                };

                Object.entries(defaults).forEach(([field, variantDefaults]) => {
                    const currentValue = options.get(field);
                    const otherDefault = variantDefaults[otherVariant];
                    const newDefault = variantDefaults[currentVariant];

                    if (currentValue === undefined || currentValue === otherDefault || !currentValue) {
                        options.set(field, newDefault);
                    }
                });
            },
        },
        {
            name: 'headline',
            type: 'string',
            friendlyName: 'Headline',
            required: true,
            defaultValue: 'Experience the Future of Laser Hair Removal',
            helperText: 'The main heading displayed at the top of the section',
        },
        {
            name: 'headlineColor',
            type: 'string',
            friendlyName: 'Headline Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Blue',
            helperText: 'Select a color for the headline text',
        },
        {
            name: 'text',
            type: 'string',
            friendlyName: 'Text',
            helperText:
                'Supporting text content below the headline. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
        },
        {
            name: 'textColor',
            type: 'string',
            friendlyName: 'Text Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Navy',
            helperText: 'Select a color for the main text content',
        },
        {
            name: 'imageSrc',
            type: 'file',
            defaultValue:
                'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F88ad4469180d4d399b5371f7b8c5ae5c',
            allowedFileTypes: ['svg'],
            friendlyName: 'Image',
            helperText: 'Upload an image or provide an image URL for the hero section',
        },
        {
            name: 'imageAltText',
            type: 'string',
            friendlyName: 'Image Alt Text',
            defaultValue: 'Image Alt Text',
            helperText: 'Alt text for the image',
            required: true,
        },
        {
            name: 'imageFlip',
            type: 'boolean',
            defaultValue: false,
            friendlyName: 'Image Display Side',
            helperText: 'Toggle image position from right to left',
        },

        {
            name: 'icons',
            type: 'list',
            friendlyName: 'Icons (Default Variant Only)',
            showIf: 'options.get("variant") === "default" || !options.get("variant")',
            onChange: (options) => {
                // Check if the list has more than 5 items
                if (options.get('items').length > 5) {
                    options.set('items', options.get('items').slice(0, 5));
                    alert('maximum items is 5, delete items to continue');
                }
                // check if the list has less than 3 items
                if (options.get('items').length < 3) {
                    alert('minimum items is 3, add items to continue');
                }
            },
            subFields: [
                {
                    name: 'iconSrc',
                    type: 'file',
                    allowedFileTypes: ['svg'],
                    friendlyName: 'Icon',
                    helperText: 'Icon image',
                    required: true,
                },
                {
                    name: 'iconText',
                    type: 'string',
                    friendlyName: 'Icon Text',
                    required: true,
                    helperText:
                        'Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
                },
            ],
            defaultValue: [
                {
                    index: 0,
                    iconSrc:
                        'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fe8cc0ff7a853473ebecdc56459f7fe11',
                    iconText:
                        'Underarm laser hair removal leaves you with smooth skin free of bumps, razor burn, prickly hair, and all that other unfun stuff.',
                },
                {
                    index: 1,
                    iconSrc:
                        'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fe8cc0ff7a853473ebecdc56459f7fe11',
                    iconText:
                        'Facial laser hair removal leaves your skin soft and smooth, making things like makeup application easier than ever.',
                },
                {
                    index: 2,
                    iconSrc:
                        'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fe8cc0ff7a853473ebecdc56459f7fe11',
                    iconText:
                        'Laser hair removal reduces hair density, regrowth, and pigmentation from shaving which can cause dark shadows on delicate underarm skin.',
                },
                {
                    index: 3,
                    iconSrc:
                        'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fe8cc0ff7a853473ebecdc56459f7fe11',
                    iconText:
                        'Advanced technology safely and effectively treats all skin tones from light skin to darker skin.',
                },
            ],
            helperText: 'List of icons to display (only visible in default variant)',
            required: false,
        },
        {
            name: 'iconColor',
            type: 'string',
            defaultValue: 'Navy',
            friendlyName: 'Icon Color (Default Variant Only)',
            helperText: 'Color change will apply to SVG icons only (only visible in default variant)',
            enum: iconColorOptions,
            showIf: 'options.get("variant") === "default" || !options.get("variant")',
        },
        {
            name: 'iconTextColor',
            type: 'string',
            defaultValue: 'Navy',
            friendlyName: 'Icon Text Color (Default Variant Only)',
            helperText: 'Select a color for the icon text (only visible in default variant)',
            enum: textColorOptionsLimited,
            showIf: 'options.get("variant") === "default" || !options.get("variant")',
        },
        {
            name: 'textSteps',
            type: 'list',
            friendlyName: 'Text Steps (Brand Partner Variant Only)',
            helperText: 'Add numbered steps with title and description (only visible in brandPartner variant)',
            required: false,
            showIf: 'options.get("variant") === "brandPartner"',
            subFields: [
                {
                    name: 'title',
                    type: 'string',
                    friendlyName: 'Step Title',
                    required: true,
                    helperText:
                        'The title for this step (maximum 60 characters). Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
                    regex: {
                        pattern: '^.{1,60}$',
                        message: 'Step title must be between 1 and 60 characters',
                    },
                },
                {
                    name: 'description',
                    type: 'string',
                    friendlyName: 'Step Description',
                    required: true,
                    helperText:
                        'The description text for this step (maximum 200 characters). Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
                    regex: {
                        pattern: '^.{1,200}$',
                        message: 'Step description must be between 1 and 200 characters',
                    },
                },
            ],
            defaultValue: [
                {
                    title: 'Book your complimentary consultation.',
                    description: 'We will get to know your goals and create a custom plan.',
                },
                {
                    title: 'Try laser hair removal for FREE.',
                    description: 'See how it works for yourself.',
                },
                {
                    title: 'Stay smooth for life.',
                    description: 'Clients are up to 95% hair-free in 7-10 treatments. Need more? They are included!',
                },
            ],
        },
        {
            name: 'optionalText',
            friendlyName: 'Bottom Text',
            type: 'string',
            defaultValue:
                'Our advanced laser technology provides precise, long-lasting results with minimal discomfort. Discover how our innovative approach is revolutionizing hair removal treatments.',
            helperText:
                'Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
        },
        {
            name: 'optionalTextColor',
            type: 'string',
            friendlyName: 'Bottom Text Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Navy',
            helperText: 'Select a color for the bottom text content',
        },
        {
            name: 'buttons',
            type: 'list',
            friendlyName: 'Buttons',
            helperText:
                'Add a call-to-action button that will appear below the section text (maximum 1 button allowed)',
            onChange: (options) => {
                const items = options.get('buttons');
                if (items.length > 1) {
                    options.set('buttons', items.slice(0, 1));
                    alert('Maximum 1 button allowed');
                }
            },
            subFields: [
                {
                    name: 'label',
                    type: 'string',
                    required: true,
                    friendlyName: 'Button Text',
                    helperText: 'The text that will appear on the button (keep it short and action-oriented)',
                    defaultValue: 'Learn More',
                },
                {
                    name: 'variant',
                    type: 'string',
                    required: true,
                    friendlyName: 'Button Variant',
                    enum: buttonVariantOptions,
                    helperText:
                        'The style of the button: primary (blue), secondary (navy blue - darker than primary), cta (special emphasis)',
                    defaultValue: 'primary',
                },
                {
                    name: 'url',
                    type: 'string',
                    required: true,
                    friendlyName: 'Button URL',
                    helperText:
                        'The destination page when the button is clicked (e.g., /services, /contact, /special-offer)',
                    defaultValue: '/process',
                },
            ],
        },
        {
            name: 'links',
            type: 'list',
            friendlyName: 'Links',
            helperText: 'Add an optional link that will appear below the section content (maximum 1 link allowed)',
            onChange: (options) => {
                const items = options.get('links');
                if (items.length > 1) {
                    options.set('links', items.slice(0, 1));
                    alert('Maximum 1 link allowed');
                }
            },
            subFields: [
                {
                    name: 'linkText',
                    type: 'string',
                    required: true,
                    friendlyName: 'Link Text',
                    helperText: 'The text that will appear as the link',
                    defaultValue: 'Why Choose Milan',
                },
                {
                    name: 'linkUrl',
                    type: 'string',
                    required: true,
                    friendlyName: 'Link URL',
                    helperText: 'The destination URL when the link is clicked',
                    defaultValue: '/process',
                },
                {
                    name: 'linkTextColor',
                    type: 'string',
                    friendlyName: 'Link Text Color',
                    enum: textColorOptionsLimited,
                    defaultValue: 'Blue',
                    helperText: 'Select a color for the link text',
                },
            ],
        },
        ...getBackgroundColorInput({
            defaultValue: 'Blue10',
        }),
        ...getBackgroundGradientInput({
            backgroundGradientList: backgroundGradientOptions,
        }),
        {
            name: 'hasBackgroundWave',
            type: 'boolean',
            friendlyName: 'Background Wave',
            defaultValue: true,
            helperText: 'Toggle background wave on or off',
        },
        {
            name: 'backgroundWaveFlip',
            type: 'boolean',
            friendlyName: 'Background Wave Flip',
            defaultValue: false,
            helperText: 'Toggle background wave position from left to right',
        },
        {
            name: 'backgroundWaveColor',
            type: 'string',
            friendlyName: 'Background Wave Color',
            enum: waveColorOptions,
            defaultValue: 'White',
            helperText: 'Select a color for the background wave, this will not work if background gradient is selected',
        },
        ...getPaddingInputs({
            desktopTop: 'none',
            desktopBottom: 'none',
            mobileTop: 'none',
            mobileBottom: 'none',
        }),
    ],
});
